import { useMemo } from 'react';
import { HealthPlanStatuses } from '@common-fe/common-fe';
import _ from 'lodash';

import { useOrganizationInfoStore } from '@/modules/employee/Employee/PersonalInformation/OrganizationInfo/stores';
import { useHealthPlanByIdQuery } from '@/modules/HealthPlan/HealthPlanSetup/queries';
import { useHealthPlansListQuery } from '@/modules/HealthPlan/queries';
import { getIdsFromPath } from '@/utils/modifiers';

interface Props {
  currentHealthPlanId?: string;
  isEdit?: boolean;
}

interface FormattedHealthPlanInterface {
  id: string;
  planYear: {
    id: string;
    name: string;
    validFrom: string;
    validTo: string;
  };
  name: string;
  nameState: string;
  planStatus: string;
}

export interface CurrentHealthPlanInterface {
  planYear?: string;
  planStartDate?: string;
  planEndDate?: string;
}

export const useGetHealthPlanEnrollmentSetupData = ({
  currentHealthPlanId,
  isEdit,
}: Props) => {
  const organizationInfo = useOrganizationInfoStore((state) => state.sourceState);

  const employerOrganizationId = useMemo(
    () => {
      const { employerId } = getIdsFromPath(organizationInfo?.organizationPath);
      return employerId;
    },
    [organizationInfo],
  );

  const { data, isLoading: orgHealthPlansLoading } = useHealthPlansListQuery(
    employerOrganizationId || undefined,
    HealthPlanStatuses.ACTIVE,
  );

  const isAddingHealthPlanEnrollmentAvailable = useMemo(() => {
    return !!data?.length;
  }, [data]);

  const { data: healthPlan, isLoading: healthPlanLoading } = useHealthPlanByIdQuery(currentHealthPlanId);

  const healthPlanCoverageOptions = useMemo(() => {
    if (healthPlan) {
      return healthPlan?.health_plan_deductible_configs.map((item) => ({
        key: `${item?.code?.code}`,
        value: item?.coverage?.name,
        title: item?.coverage?.name,
      }));
    }

    return [];
  }
  , [healthPlan]);

  const formattedHealthPlan = useMemo<FormattedHealthPlanInterface | null>(() => {
    if (healthPlan) {
      return {
        id: _.toString(healthPlan.id),
        planYear: {
          id: _.toString(healthPlan.plan_year.id),
          name: healthPlan.plan_year?.name ?? '',
          validFrom: healthPlan.plan_year?.valid_from ?? '',
          validTo: healthPlan.plan_year?.valid_to ?? '',
        },
        name: healthPlan?.name.name ?? '',
        nameState: healthPlan?.name.name_state ?? '',
        planStatus: healthPlan?.plan_status ?? '',
      };
    }

    return null;
  }, [healthPlan]);

  const healthPlansOptions = useMemo(() => data?.map((plan) => ({
    key: plan.id,
    value: plan.name,
    title: plan.name,
  })), [data]);

  const currentHealthPlan = useMemo<CurrentHealthPlanInterface>(() => {
    const currPlan = data?.find((plan) => plan.id === currentHealthPlanId);

    if (isEdit && formattedHealthPlan) {
      return {
        planYear: formattedHealthPlan.planYear.name,
        planStartDate: healthPlan?.plan_year.valid_from,
        planEndDate: healthPlan?.plan_year.valid_to,
      };
    }

    return {
      planYear: currPlan?.planYearName,
      planStartDate: healthPlan?.plan_year.valid_from,
      planEndDate: healthPlan?.plan_year.valid_to,
    };
  }, [
    currentHealthPlanId,
    data,
    isEdit,
    healthPlan,
    formattedHealthPlan,
  ]);

  return {
    healthPlansOptions,
    healthPlanLoading,
    currentHealthPlan,
    healthPlanCoverageOptions,
    isAddingHealthPlanEnrollmentAvailable,
    isLoading : orgHealthPlansLoading,
  };
};
